/**
 * ColorPickerPaletteTile  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";

import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";

import { ColorPickerTile } from "./ColorPickerTile";
import { getColorUtils } from "./colorUtils";

export interface IColorPickerPaletteTileProps {
	value?: string;
	label?: string;
	selected?: boolean;
	className?: string;
	onClick?: (value: string) => void;
}

export const ColorPickerPaletteTile: React.FC<IColorPickerPaletteTileProps> = ({
	className,
	value,
	label,
	selected,
	onClick
}) => {
	const color = getColorUtils(value);
	const baseClass = BEM("edt-color-picker-palette-tile", null, {
		selected,
		light: color.isLight(),
		dark: color.isDark(),
		transparent: color.getAlpha() === 0
	});

	const handleClick = useCallback(() => {
		if (isFunction(onClick)) {
			onClick(value);
		}
	}, [ onClick, value ]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<ColorPickerTile
			value={value}
			className={baseClass.toClassName()}
			onClick={handleClick}
			title={label || `${value || ""}`}
		>
			<div className={baseClass.element("inner").toClassName()}>
				<span className={baseClass.element("label").toClassName()}>{label || value}</span>
			</div>
		</ColorPickerTile>
	);
};
